"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeAddressElementOptions } from "@stripe/stripe-js";
import { getStripeBrowser } from "@/lib/stripe-client";
import { fetchWithAuth } from "@/lib/auth";
import { releaseStrandedDialogScrollLock } from "@/lib/release-dialog-scroll-lock";
import {
  clearOperationKey,
  isTerminalMutationFailure,
  operationKeyFor,
  type OperationKeySlot,
} from "@/lib/mutation-idempotency";
import {
  mergeStripeDepositReturnStash,
  paymentIntentIdFromClientSecret,
} from "@/lib/open-deposits";
import { confirmWalletDeposit } from "@/lib/stripe-wallet-confirm";
import {
  currentMarketingClickIds,
  isMarketingConsentAllowed,
} from "@/lib/marketing-attribution";
import { isAnalyticsAllowed } from "@/lib/analytics-consent";
import { DEPOSIT_LADDER_CENTS, DEPOSIT_LADDER_MIN_CENTS, snapDepositToLadder } from "@/lib/deposit-ladder";

const PRESET_CENTS = DEPOSIT_LADDER_CENTS;

/** Stack above cookie/support (z-100) and legacy dialogs (z-200). */
const MODAL_BACKDROP_Z = 600;
const MODAL_PANEL_Z = 601;

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

// The ladder rungs are quick-pick presets only, not a whitelist: a typed
// custom amount is used exactly as entered (any whole-cent value), never
// snapped or forced onto a rung. The server enforces the real floor
// ($25 for every deposit).
function effectiveAmountCents(amountCents: number, custom: string): number {
  if (custom.trim() !== "") {
    const n = parseFloat(custom.replace(/[^0-9.]/g, ""));
    const parsed = Math.round(n * 100);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : amountCents;
  }
  return amountCents;
}

export type AddFundsCheckoutHandle = {
  submit: () => Promise<void>;
};

/** Prefill for the billing AddressElement, sourced from the user's last saved billing details. */
export type BillingDefaultValues = NonNullable<StripeAddressElementOptions["defaultValues"]>;

const CheckoutInner = forwardRef<
  AddFundsCheckoutHandle,
  {
    onSuccess: () => void;
    onBusyChange: (busy: boolean) => void;
    onReadyChange: (ready: boolean) => void;
    onAddressReadyChange: (ready: boolean) => void;
    onStripeErr: (msg: string | null) => void;
    onBeforeConfirm: () => void;
    addressDefaultValues?: BillingDefaultValues;
  }
>(function CheckoutInner(
  { onSuccess, onBusyChange, onReadyChange, onAddressReadyChange, onStripeErr, onBeforeConfirm, addressDefaultValues },
  ref,
) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    onReadyChange(Boolean(stripe && elements));
  }, [stripe, elements, onReadyChange]);

  const submit = useCallback(async () => {
    if (!stripe || !elements) return;
    onStripeErr(null);
    onBusyChange(true);
    try {
      onBeforeConfirm();
      const { error } = await confirmWalletDeposit(stripe, elements);
      if (error) {
        onStripeErr(error.message ?? "Payment failed");
        return;
      }
      onSuccess();
    } finally {
      onBusyChange(false);
    }
  }, [stripe, elements, onSuccess, onBusyChange, onStripeErr, onBeforeConfirm]);

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  return (
    <div className="flex flex-col gap-4">
      {/*
       * Mounted in the same Elements instance as the PaymentElement below, so
       * Stripe automatically attaches the collected name + address as
       * `billing_details` on `stripe.confirmPayment` — no manual wiring
       * needed. This is what puts a billing identity on the charge for
       * Radar scoring, AVS, and dispute evidence.
       */}
      <AddressElement
        options={{ mode: "billing", display: { name: "full" }, defaultValues: addressDefaultValues }}
        onChange={(event) => onAddressReadyChange(event.complete)}
      />
      <PaymentElement options={{ layout: "tabs" }} />
    </div>
  );
});
CheckoutInner.displayName = "CheckoutInner";

export function AddFundsModal({
  open,
  onOpenChange,
  suggestedCents,
  mode = "create",
  continueClientSecret = null,
  continueAmountCents = null,
  stripePublishableKey,
  preConfirmBalanceCents = null,
  onSuccess,
  onWriteRejected,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  suggestedCents?: number;
  mode?: "create" | "continue";
  continueClientSecret?: string | null;
  continueAmountCents?: number | null;
  stripePublishableKey: string;
  /** Wallet balance known before confirmPayment (survives Stripe redirect via session stash). */
  preConfirmBalanceCents?: number | null;
  onSuccess: (detail: { amountCents: number }) => void;
  onWriteRejected?: (status: number, errorCode?: string) => void;
}) {
  const titleId = useId();
  const descId = useId();
  const [portalReady, setPortalReady] = useState(false);

  const [step, setStep] = useState<"amount" | "pay">("amount");
  const [amountCents, setAmountCents] = useState(2_500);
  const [custom, setCustom] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [payAmountCents, setPayAmountCents] = useState<number | null>(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [intentErr, setIntentErr] = useState<string | null>(null);
  const [stripeErr, setStripeErr] = useState<string | null>(null);
  const [payBusy, setPayBusy] = useState(false);
  const [payReady, setPayReady] = useState(false);
  const [addressReady, setAddressReady] = useState(false);
  const [billingDefaultValues, setBillingDefaultValues] = useState<BillingDefaultValues | undefined>(undefined);
  const checkoutRef = useRef<AddFundsCheckoutHandle | null>(null);
  const depositOperationRef = useRef<OperationKeySlot>(null);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  // Prefill the billing AddressElement from whatever was saved on a prior
  // deposit, so a returning user does not retype their name and address.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchWithAuth("/api/v1/wallet");
        const body = (await res.json().catch(() => ({}))) as {
          data?: {
            billingDetails?: {
              firstName?: string;
              lastName?: string;
              addressLine1?: string;
              addressLine2?: string;
              addressCity?: string;
              addressState?: string;
              addressPostalCode?: string;
              addressCountry?: string;
            };
          };
        };
        if (cancelled || !res.ok) return;
        const b = body.data?.billingDetails;
        if (!b) return;
        const name = [b.firstName, b.lastName].filter(Boolean).join(" ");
        setBillingDefaultValues({
          name: name || undefined,
          address: {
            line1: b.addressLine1 ?? "",
            line2: b.addressLine2 ?? "",
            city: b.addressCity ?? "",
            state: b.addressState ?? "",
            postal_code: b.addressPostalCode ?? "",
            country: b.addressCountry ?? "",
          },
        });
      } catch {
        // Prefill is a convenience only; leave the AddressElement blank on failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open && suggestedCents != null && mode === "create") {
      setAmountCents(snapDepositToLadder(suggestedCents));
    }
  }, [open, suggestedCents, mode]);

  const isContinuePay = mode === "continue" && Boolean(continueClientSecret);
  const effectiveStep = isContinuePay ? "pay" : step;
  const effectiveClientSecret = isContinuePay ? continueClientSecret : clientSecret;
  const effectivePayAmountCents = isContinuePay ? (continueAmountCents ?? null) : payAmountCents;

  useEffect(() => {
    if (!open) {
      setStep("amount");
      setClientSecret(null);
      setPayAmountCents(null);
      setIntentErr(null);
      setStripeErr(null);
      setPayBusy(false);
      setPayReady(false);
      setAddressReady(false);
      releaseStrandedDialogScrollLock();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const stripePromise = useMemo(() => {
    if (!stripePublishableKey.startsWith("pk_")) return null;
    return getStripeBrowser(stripePublishableKey);
  }, [stripePublishableKey]);

  const centsPreview = effectiveAmountCents(amountCents, custom);

  const stashPreConfirmBaseline = useCallback(() => {
    const secret = effectiveClientSecret;
    const payCents = effectivePayAmountCents ?? centsPreview;
    mergeStripeDepositReturnStash({
      paymentIntentId: secret ? paymentIntentIdFromClientSecret(secret) : null,
      redirectStatus: null,
      balanceCentsBefore: preConfirmBalanceCents,
      amountCents: payCents > 0 ? payCents : null,
    });
  }, [effectiveClientSecret, effectivePayAmountCents, centsPreview, preConfirmBalanceCents]);

  const startPay = useCallback(async () => {
    if (mode === "continue") return;
    setIntentErr(null);
    const cents = effectiveAmountCents(amountCents, custom);
    if (!Number.isFinite(cents) || cents < DEPOSIT_LADDER_MIN_CENTS) {
      setIntentErr("Minimum deposit is $25.00");
      return;
    }
    if (!stripePromise) {
      setIntentErr("Stripe is not configured.");
      return;
    }
    setLoadingIntent(true);
    try {
      const marketingConsent = isMarketingConsentAllowed();
      const analyticsConsent = isAnalyticsAllowed();
      const clickIds = marketingConsent ? currentMarketingClickIds() : undefined;
      const operationKey = operationKeyFor(
        depositOperationRef,
        JSON.stringify({ amountCents: cents, analyticsConsent, marketingConsent, clickIds }),
      );
      const res = await fetchWithAuth("/api/v1/wallet/deposit/intent", {
        method: "POST",
        headers: { "Idempotency-Key": operationKey },
        body: JSON.stringify({
          amountCents: cents,
          analyticsConsent,
          marketingConsent,
          clickIds,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (isTerminalMutationFailure(res.status)) clearOperationKey(depositOperationRef);
        const errBody = body as { error?: { message?: string; code?: string } };
        onWriteRejected?.(res.status, errBody.error?.code);
        setIntentErr(errBody.error?.message ?? "Could not start payment");
        return;
      }
      const secret = (body as { data?: { clientSecret?: string } }).data?.clientSecret;
      if (!secret) {
        setIntentErr("No client secret returned");
        return;
      }
      clearOperationKey(depositOperationRef);
      setPayAmountCents(cents);
      setClientSecret(secret);
      setStep("pay");
    } catch {
      // Do not rotate the key after a transport failure: the API may have
      // created the PaymentIntent after the browser lost the response.
      setIntentErr("Could not start payment. Please retry.");
    } finally {
      setLoadingIntent(false);
    }
  }, [amountCents, custom, stripePromise, mode, onWriteRejected]);

  const goBackToAmount = useCallback(() => {
    setStep("amount");
    setClientSecret(null);
    setPayAmountCents(null);
    setStripeErr(null);
    setPayReady(false);
    setAddressReady(false);
  }, []);

  const primaryFooterAction = useCallback(async () => {
    if (effectiveStep === "amount") {
      if (mode === "continue") return;
      await startPay();
      return;
    }
    await checkoutRef.current?.submit();
  }, [effectiveStep, mode, startPay]);

  // Live minimum check so the rule is visible while typing rather than
  // only after a failed Continue.
  const amountBelowMinimum =
    effectiveStep === "amount" &&
    mode !== "continue" &&
    Number.isFinite(centsPreview) &&
    centsPreview > 0 &&
    centsPreview < DEPOSIT_LADDER_MIN_CENTS;
  const footerError =
    effectiveStep === "amount"
      ? (intentErr ?? (amountBelowMinimum ? "Minimum deposit is $25.00" : null))
      : stripeErr;
  const payLabelCents = effectivePayAmountCents ?? centsPreview;

  const panel = (
    <div
      className={`fixed left-1/2 top-1/2 flex max-h-[min(90vh,900px)] w-[calc(100vw-1.5rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border-2 border-mot-teal/40 bg-[#101418] text-mot-white shadow-[0_30px_80px_rgba(0,0,0,0.85)] sm:max-h-[85vh] ${
        effectiveStep === "pay" ? "max-w-xl" : "max-w-md"
      }`}
      style={{ zIndex: MODAL_PANEL_Z }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <header className="flex shrink-0 items-start gap-3 border-b border-dark-border px-6 pb-4 pt-6">
        {effectiveStep === "pay" && mode === "create" ? (
          <button
            type="button"
            onClick={goBackToAmount}
            className="-ml-1 mt-0.5 rounded-lg border border-dark-border bg-dark-card px-2.5 py-1.5 text-xs text-mot-grey-light transition-colors hover:border-mot-teal/30 hover:text-mot-teal"
            aria-label="Back to amount"
          >
            ←
          </button>
        ) : (
          <span className="w-8 shrink-0" aria-hidden />
        )}
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <h2 id={titleId} className="text-lg font-semibold text-mot-white">
            Add funds
          </h2>
          <p id={descId} className="text-sm text-mot-grey">
            Add dollars to your account for packs and platform purchases.
          </p>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
        {effectiveStep === "amount" ? (
          <div className="flex flex-col gap-4">
            <div className="text-center text-4xl font-black text-mot-grey-light sm:text-left">
              {formatMoney(centsPreview)}
            </div>
            <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
              {PRESET_CENTS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCustom("");
                    setAmountCents(c);
                  }}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                    amountCents === c && custom.trim() === ""
                      ? "border-mot-teal bg-mot-teal/10 text-mot-teal"
                      : "border-dark-border bg-dark-card text-mot-grey-light"
                  }`}
                >
                  {formatMoney(c)}
                </button>
              ))}
            </div>
            <label className="flex flex-col gap-1 text-left text-sm">
              <span className="text-mot-grey">Custom amount (USD)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 42.00"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-mot-white"
              />
            </label>
          </div>
        ) : effectiveClientSecret && stripePromise ? (
          <Elements
            key={effectiveClientSecret}
            stripe={stripePromise}
            options={{
              clientSecret: effectiveClientSecret,
              // Stripe renders its own labels; the default light-theme grey is
              // unreadable on the dark modal.
              appearance: { rules: { ".Label": { color: "#ffffff" } } },
            }}
          >
            <CheckoutInner
              ref={checkoutRef}
              onBeforeConfirm={stashPreConfirmBaseline}
              onSuccess={() => {
                onSuccess({ amountCents: effectivePayAmountCents ?? 0 });
                onOpenChange(false);
              }}
              onBusyChange={setPayBusy}
              onReadyChange={setPayReady}
              onAddressReadyChange={setAddressReady}
              onStripeErr={setStripeErr}
              addressDefaultValues={billingDefaultValues}
            />
          </Elements>
        ) : (
          <p className="text-sm text-red-400">Unable to load payment form.</p>
        )}
      </div>

      <footer className="shrink-0 border-t border-dark-border bg-mot-black px-6 py-4">
        {footerError ? <p className="mb-3 text-sm text-red-400">{footerError}</p> : null}
        {effectiveStep === "pay" && !(effectiveClientSecret && stripePromise) ? (
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-full rounded-xl border border-dark-border bg-dark-card py-3 font-bold text-mot-white transition-colors hover:border-mot-teal/30"
          >
            Close
          </button>
        ) : (
          <button
            type="button"
            disabled={effectiveStep === "amount" ? loadingIntent || amountBelowMinimum : !payReady || !addressReady || payBusy}
            onClick={() => void primaryFooterAction()}
            className="w-full rounded-xl bg-mot-teal py-3 font-bold text-white shadow-lg shadow-mot-teal/20 transition-all hover:bg-mot-teal-light disabled:opacity-50"
          >
            {effectiveStep === "amount" ?
              loadingIntent ?
                "Loading…"
              : "Continue"
            : payBusy ?
              "Processing…"
            : `Pay ${formatMoney(payLabelCents)}`}
          </button>
        )}
      </footer>
    </div>
  );

  const overlay = (
    <>
      <button
        type="button"
        className="fixed inset-0 cursor-default bg-black/75"
        style={{ zIndex: MODAL_BACKDROP_Z }}
        aria-label="Close add funds"
        onClick={() => onOpenChange(false)}
      />
      {panel}
    </>
  );

  if (!portalReady || typeof document === "undefined") return null;

  return createPortal(open ? overlay : null, document.body);
}
